import { useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { ChevronsLeftRight, MoveRight } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { SLIDER_PROJECTS, FENCE_PAIR, GALLERY } from "./data";
import Lightbox from "./Lightbox";

export const CompareSlider = ({ before, after, title, showLabels, testid }) => {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const setFromClientX = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, p)));
  };

  return (
    <div
      ref={ref}
      data-testid={testid}
      role="slider"
      tabIndex={0}
      aria-label={`${title} before and after comparison slider`}
      aria-valuenow={Math.round(pos)}
      aria-valuemin={0}
      aria-valuemax={100}
      onPointerDown={(e) => {
        dragging.current = true;
        ref.current.setPointerCapture(e.pointerId);
        setFromClientX(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && setFromClientX(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 4));
        if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 4));
      }}
      className="group relative aspect-[4/3] w-full cursor-ew-resize touch-none select-none overflow-hidden rounded-t-2xl outline-none focus-visible:ring-2 focus-visible:ring-gold"
    >
      <img src={after} alt={`${title} — after`} draggable={false} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
      <img
        src={before}
        alt={`${title} — before`}
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        loading="lazy"
      />
      <div className="absolute inset-y-0" style={{ left: `${pos}%` }} aria-hidden="true">
        <div className="absolute inset-y-0 -left-px w-[3px] bg-gold shadow-[0_0_14px_rgba(212,175,55,0.9)]" />
        <div className="absolute top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-gold text-navy shadow-lg transition-transform duration-200 group-active:scale-110">
          <ChevronsLeftRight size={18} />
        </div>
      </div>
      {showLabels && (
        <>
          <span className="absolute left-3 top-3 rounded-full bg-navy-dark/80 px-3 py-1 font-mono text-[10px] tracking-[0.22em] text-white backdrop-blur-sm">BEFORE</span>
          <span className="absolute right-3 top-3 rounded-full bg-gold px-3 py-1 font-mono text-[10px] tracking-[0.22em] text-navy">AFTER</span>
        </>
      )}
    </div>
  );
};

const Work = () => {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="work" data-testid="work-section" className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHead num="03" eyebrow="Our Work" title="Real Projects. Real Transformations." testid="work-head" />
          <Reveal delay={0.15} className="max-w-sm">
            <p className="text-sm leading-relaxed text-slate-500 sm:text-base">
              Actual Teo's Painting project photos — drag the gold handle to compare before and after.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-8 md:grid-cols-2 xl:grid-cols-3">
          {SLIDER_PROJECTS.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <article
                data-testid={`project-card-${p.slug}`}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_10px_35px_-18px_rgba(11,25,44,0.25)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_28px_60px_-24px_rgba(11,25,44,0.35)]"
              >
                <CompareSlider
                  before={p.before}
                  after={p.after}
                  title={p.title}
                  showLabels={!p.bakedLabels}
                  testid={`compare-slider-${p.slug}`}
                />
                <div className="p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-muted">{p.location}</p>
                  <h3 className="mt-2 font-display text-xl font-bold text-navy">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-10">
          <article
            data-testid="project-card-fence-staining"
            className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_10px_35px_-18px_rgba(11,25,44,0.25)]"
          >
            <div className="relative grid sm:grid-cols-2">
              <div className="relative">
                <img src={FENCE_PAIR.before} alt="Fence before staining" className="aspect-[4/3] w-full object-cover" loading="lazy" />
                <span className="absolute left-3 top-3 rounded-full bg-navy-dark/80 px-3 py-1 font-mono text-[10px] tracking-[0.22em] text-white backdrop-blur-sm">BEFORE</span>
              </div>
              <div className="relative">
                <img src={FENCE_PAIR.after} alt="Fence after staining" className="aspect-[4/3] w-full object-cover" loading="lazy" />
                <span className="absolute right-3 top-3 rounded-full bg-gold px-3 py-1 font-mono text-[10px] tracking-[0.22em] text-navy">AFTER</span>
              </div>
              <div aria-hidden="true" className="absolute left-1/2 top-1/2 hidden h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-gold text-navy shadow-xl sm:flex">
                <MoveRight size={20} />
              </div>
            </div>
            <div className="p-6 sm:p-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-muted">{FENCE_PAIR.location}</p>
              <h3 className="mt-2 font-display text-2xl font-bold text-navy">{FENCE_PAIR.title}</h3>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">{FENCE_PAIR.desc}</p>
            </div>
          </article>
        </Reveal>

        <div className="mt-24">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <SectionHead num="04" eyebrow="Gallery" title="Project Gallery" testid="gallery-head" />
            <Reveal delay={0.15}>
              <p className="text-sm text-slate-500">Tap any photo to view it larger.</p>
            </Reveal>
          </div>

          <div className="mt-12 columns-2 gap-4 md:columns-3" data-testid="gallery-grid">
            {GALLERY.map((g, i) => (
              <Reveal key={g.src} delay={(i % 3) * 0.06} className="mb-4 break-inside-avoid">
                <button
                  data-testid={`gallery-item-${i}`}
                  onClick={() => setLightbox(i)}
                  aria-label={`Open photo: ${g.title}`}
                  className="group relative block w-full overflow-hidden rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                >
                  <img
                    src={g.src}
                    alt={g.title}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                  />
                  <span className="absolute inset-0 flex items-end bg-gradient-to-t from-navy-dark/85 via-navy-dark/10 to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <span className="text-left">
                      <span className="block font-mono text-[9px] uppercase tracking-[0.25em] text-gold">{g.cat}</span>
                      <span className="mt-1 block text-sm font-semibold text-white">{g.title}</span>
                    </span>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {lightbox !== null && (
          <Lightbox
            items={GALLERY}
            index={lightbox}
            onClose={() => setLightbox(null)}
            onNav={(dir) => setLightbox((cur) => (cur + dir + GALLERY.length) % GALLERY.length)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Work;
